import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Button, DataTable, FullBox, Page, Spinner, Typography, useOverlayContext } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import ExternalLinkButton from '~/components/ExternalLinkButton';
import { useTableColumnMenu } from '~/hooks/useTableColumnMenu';
import { useLocaleKeys } from '~/i18n';
import { APIKeysEmptyState } from './APIKeysEmptyState';
import { APIKeysSortField, toRows, toSortField } from './APIKeysPage.utils';
import { useAPIKeys } from './hooks/useAPIKeys';
import { showGenerateAPIKeyModal, toAPIKeyMenuActions } from './modals/apiKeyModals';
export const GetAPIKeysPageTID = (parentTestId)=>getTestIds(parentTestId);
const APIKeysSubheader = ()=>{
    const { t: apiKeysPageTexts } = useLocaleKeys('settings.apiKeys');
    const { APIKeysPageTID } = useMemo(()=>GetAPIKeysPageTID(), []);
    return /*#__PURE__*/ _jsxs(Typography, {
        children: [
            apiKeysPageTexts.accessSubheader(),
            ' ',
            /*#__PURE__*/ _jsx(ExternalLinkButton, {
                href: "https://docs.sayanchor.com",
                underline: "none",
                openInNewTab: true,
                testId: APIKeysPageTID.DocsLink(),
                bi: {
                    name: 'anchor_api_docs_link'
                },
                children: apiKeysPageTexts.anchorApi()
            })
        ]
    });
};
export const APIKeysPage = ()=>{
    const { t: apiKeysPageTexts } = useLocaleKeys('settings.apiKeys');
    const overlayContext = useOverlayContext();
    const { APIKeysPageTID } = useMemo(()=>GetAPIKeysPageTID(), []);
    const [sort, setSort] = useState({
        field: APIKeysSortField.Name,
        direction: 'asc'
    });
    const { keys, isLoading, hasError, refresh } = useAPIKeys({
        sort
    });
    const columnMenu = useTableColumnMenu('api_keys');
    const modalsContext = {
        overlayContext,
        keys,
        refresh
    };
    if (isLoading) {
        return /*#__PURE__*/ _jsx(Page, {
            header: apiKeysPageTexts.header(),
            subheader: /*#__PURE__*/ _jsx(APIKeysSubheader, {}),
            testId: APIKeysPageTID.Page(),
            children: /*#__PURE__*/ _jsx(Spinner, {
                size: 24
            })
        });
    }
    if (hasError) {
        return /*#__PURE__*/ _jsx(Page, {
            header: apiKeysPageTexts.header(),
            subheader: /*#__PURE__*/ _jsx(APIKeysSubheader, {}),
            testId: APIKeysPageTID.Page(),
            children: /*#__PURE__*/ _jsx(FullBox, {
                testId: APIKeysPageTID.Error(),
                children: /*#__PURE__*/ _jsx(Typography, {
                    children: apiKeysPageTexts.errorLoading()
                })
            })
        });
    }
    return /*#__PURE__*/ _jsx(Page, {
        header: apiKeysPageTexts.header(),
        variant: "containedHeight",
        disableLeftGutter: true,
        disableRightGutter: true,
        disableBottomPadding: true,
        subheader: /*#__PURE__*/ _jsx(APIKeysSubheader, {}),
        actions: keys.length > 0 ? /*#__PURE__*/ _jsx(Button, {
            bi: {
                name: 'generate_api_key'
            },
            testId: APIKeysPageTID.GenerateButton(),
            onClick: ()=>showGenerateAPIKeyModal(modalsContext),
            children: apiKeysPageTexts.generateApiKey()
        }) : null,
        testId: APIKeysPageTID.Page(),
        children: keys.length > 0 ? /*#__PURE__*/ _jsx(DataTable, {
            allowColumnResize: true,
            allowColumnReorder: true,
            reorderBi: {
                name: 'api_keys_reorder_columns'
            },
            allowColumnHide: true,
            columnMenu: columnMenu,
            mainColumn: {
                type: 'text',
                name: 'name',
                header: apiKeysPageTexts.columnName(),
                width: {
                    initial: 300,
                    min: 300
                },
                sortable: true,
                bi: {
                    name: 'api_keys_by_name'
                }
            },
            columns: [
                {
                    type: 'text',
                    name: 'value',
                    header: apiKeysPageTexts.columnValue(),
                    width: {
                        initial: 240,
                        min: 240
                    }
                },
                {
                    type: 'date',
                    name: 'created',
                    header: apiKeysPageTexts.columnCreated(),
                    sortable: true,
                    bi: {
                        name: 'api_keys_by_created'
                    }
                },
                {
                    type: 'date',
                    name: 'updated',
                    header: apiKeysPageTexts.columnUpdated(),
                    sortable: true,
                    bi: {
                        name: 'api_keys_by_updated'
                    }
                }
            ],
            items: toRows(keys),
            getRowId: (row)=>row.id,
            mobile: {
                fields: [
                    {
                        name: 'name'
                    },
                    {
                        name: 'value',
                        label: apiKeysPageTexts.columnValue()
                    },
                    {
                        name: 'created',
                        label: apiKeysPageTexts.columnCreated()
                    },
                    {
                        name: 'updated',
                        label: apiKeysPageTexts.columnUpdated()
                    }
                ]
            },
            sort: {
                name: sort.field,
                direction: sort.direction,
                sortedBy: 'consumer',
                onChange: (next)=>setSort({
                        field: toSortField(next.name),
                        direction: next.direction
                    })
            },
            rowActions: {
                menuBi: {
                    name: 'api_key_actions'
                },
                menu: (row)=>{
                    const apiKey = keys.find((key)=>key.id === row.id);
                    return apiKey == null ? [] : toAPIKeyMenuActions(modalsContext, apiKey, apiKeysPageTexts);
                }
            },
            testId: APIKeysPageTID.Table()
        }) : /*#__PURE__*/ _jsx(APIKeysEmptyState, {
            generateKey: ()=>showGenerateAPIKeyModal(modalsContext)
        })
    });
};
